import type { Endpoint, HttpMethod, ParsedSpec } from "./types";
import type { OpenAPIV3 } from "openapi-types";

const METHODS = [
  "get",
  "post",
  "put",
  "delete",
  "patch",
  "head",
  "options",
  "trace",
] as const;

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

/**
 * Simple dereferencer that resolves $ref pointers within the same document.
 * Does not handle external file references.
 */
function resolveRefs<T>(obj: T, root: T, visited = new Set<unknown>()): T {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  // Handle circular references
  if (visited.has(obj)) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => resolveRefs(item, root, visited)) as unknown as T;
  }

  visited.add(obj);
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key === "$ref" && typeof value === "string" && value.startsWith("#")) {
      // Resolve local reference
      const path = value.slice(1).split("/").filter(Boolean);
      let current: unknown = root;
      for (const segment of path) {
        if (current === null || typeof current !== "object") {
          break;
        }
        current = (current as Record<string, unknown>)[segment];
      }
      return resolveRefs(current as T, root, visited);
    }
    result[key] = resolveRefs(value, root, visited);
  }

  return result as T;
}

function parseOpenAPISpec(text: string, specId: string): ParsedSpec {
  const raw = JSON.parse(text) as OpenAPIV3.Document;

  // Resolve $ref pointers for serialization
  const parsed = resolveRefs(raw, raw);

  const title = parsed.info?.title || "Untitled API";
  const version = parsed.info?.version || "1.0.0";
  const description = parsed.info?.description;

  const endpoints: Endpoint[] = [];

  // Parse paths
  if (parsed.paths) {
    for (const [path, pathItem] of Object.entries(parsed.paths)) {
      if (!pathItem) continue;

      for (const method of METHODS) {
        const operation = (pathItem as Record<string, OpenAPIV3.OperationObject>)[
          method
        ];
        if (!operation) continue;

        const endpoint: Endpoint = {
          id: `${specId}-${method}-${path}`,
          path,
          method: method as HttpMethod,
          operation,
          specId,
          specTitle: title,
          tags: operation.tags || [],
          summary: operation.summary,
          description: operation.description,
          deprecated: operation.deprecated,
          parameters: [
            ...(pathItem.parameters || []),
            ...(operation.parameters || []),
          ] as OpenAPIV3.ParameterObject[],
          requestBody: operation.requestBody as
            | OpenAPIV3.RequestBodyObject
            | undefined,
          responses: operation.responses as
            | OpenAPIV3.ResponsesObject
            | undefined,
        };

        endpoints.push(endpoint);
      }
    }
  }

  return {
    id: specId,
    title,
    version,
    description,
    endpoints,
    raw: parsed,
  };
}

export async function parseSpecFile(file: File): Promise<ParsedSpec> {
  const text = await file.text();
  const specId = generateId();
  return parseOpenAPISpec(text, specId);
}

export async function parseSpecFromURL(url: string): Promise<ParsedSpec> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
  }
  const text = await response.text();
  const specId = generateId();
  return parseOpenAPISpec(text, specId);
}

export function getMethodColor(method: HttpMethod): string {
  const colors: Record<HttpMethod, string> = {
    get: "#10b981", // emerald-500
    post: "#3b82f6", // blue-500
    put: "#f59e0b", // amber-500
    delete: "#ef4444", // red-500
    patch: "#8b5cf6", // violet-500
    head: "#6b7280", // gray-500
    options: "#14b8a6", // teal-500
    trace: "#ec4899", // pink-500
  };
  return colors[method] || "#6b7280";
}

export function getMethodBgColor(method: HttpMethod): string {
  const colors: Record<HttpMethod, string> = {
    get: "rgba(16, 185, 129, 0.1)",
    post: "rgba(59, 130, 246, 0.1)",
    put: "rgba(245, 158, 11, 0.1)",
    delete: "rgba(239, 68, 68, 0.1)",
    patch: "rgba(139, 92, 246, 0.1)",
    head: "rgba(107, 114, 128, 0.1)",
    options: "rgba(20, 184, 166, 0.1)",
    trace: "rgba(236, 72, 153, 0.1)",
  };
  return colors[method] || "rgba(107, 114, 128, 0.1)";
}

export type { ParsedSpec, Endpoint, HttpMethod };
